import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Button,
  Dialog,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import JsonDiffViewer from './JsonDiffViewer';
import EditConfigModal from './EditConfigModal';
import { RelevanceGateCard, HoldoutCard } from './GateEvidence';
import LinkedFieldNotes from './LinkedFieldNotes';
import { skippedGateCode } from '../utils/healingGate';
import { AutoApprovalBadge, hasAutoApprovalBadge } from './StatusBadge';
import { isNotApplicable, verdictColors, verdictScoreText } from '../utils/verdictPresentation';
import { alignParsedConfigDiff, formatParsedResult, parsedForSample } from '../utils/formatParsed';

/* ================================================================== */
/*  Collapsible Section                                                */
/* ================================================================== */

function Section({ title, defaultOpen = false, count, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', py: 1.25, px: 3,
          '&:hover': { background: 'rgba(255,255,255,0.015)' },
        }}
      >
        <IconButton size="small" sx={{ color: '#475569', p: 0 }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        <Typography sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.8rem', flex: 1 }}>{title}</Typography>
        {count != null && (
          <Typography sx={{ color: '#475569', fontSize: '0.72rem' }}>{count}</Typography>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/* ================================================================== */
/*  Sample status helpers                                              */
/* ================================================================== */

const STATUS_CONFIG = {
  fixed: { label: 'Fixed', color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', icon: CheckCircleIcon },
  unfixed: { label: 'Unfixed', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)', icon: ErrorIcon },
  false_positive: { label: 'False Positive', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', icon: HelpOutlineIcon },
  passing: { label: 'Passing', color: '#64748b', bg: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.06)', icon: CheckCircleIcon },
  regression: { label: 'Regression', color: '#f97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.2)', icon: ReportProblemIcon },
};

function SampleResultRow({ result, parsed }) {
  const cfg = STATUS_CONFIG[result.status] || STATUS_CONFIG.unfixed;
  const Icon = cfg.icon;
  const parsedLine = formatParsedResult(parsed);
  const formatValue = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      py: 1, px: 1.5, borderRadius: 1,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      mb: 0.75,
      overflow: 'hidden',
    }}>
      <Icon sx={{ color: cfg.color, fontSize: 16, mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={cfg.label} size="small" sx={{
            height: 20, fontSize: '0.65rem', fontWeight: 600,
            color: cfg.color, background: 'transparent',
            border: `1px solid ${cfg.border}`,
          }} />
          <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontFamily: 'monospace' }}>
            #{result.sample_index}
            {result.is_passing_sample ? ' (regression test)' : ''}
          </Typography>
        </Box>
        <a
          href={result.html_url}
          target="_blank"
          rel="noreferrer"
          style={{
            color: '#60a5fa', textDecoration: 'none', fontSize: '0.78rem',
            wordBreak: 'break-all', overflowWrap: 'anywhere', display: 'block', marginTop: 2,
          }}
        >
          {result.html_url}
        </a>
        {(result.expected_value != null || result.extracted_value != null || parsedLine) && (
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            {result.expected_value != null && (
              <Typography sx={{
                color: '#64748b', fontSize: '0.72rem', lineHeight: 1.45,
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                <span style={{ color: '#475569' }}>expected:</span> {formatValue(result.expected_value)}
              </Typography>
            )}
            {result.extracted_value != null && (
              <Typography sx={{
                color: '#64748b', fontSize: '0.72rem', lineHeight: 1.45,
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                <span style={{ color: '#475569' }}>got:</span> {formatValue(result.extracted_value)}
              </Typography>
            )}
            {parsedLine && (
              <Typography sx={{
                color: parsedLine.ok ? '#64748b' : '#ef4444', fontSize: '0.72rem', lineHeight: 1.45,
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                <span style={{ color: '#475569' }}>parsed:</span> {parsedLine.text}
              </Typography>
            )}
          </Box>
        )}
        {result.reason && (
          <Typography sx={{
            color: '#64748b', fontSize: '0.72rem', mt: 0.25, fontStyle: 'italic',
            wordBreak: 'break-all', overflowWrap: 'anywhere',
          }}>
            {result.reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SampleResultsSummary({ results }) {
  if (!results || results.length === 0) return null;

  const counts = { fixed: 0, unfixed: 0, false_positive: 0, passing: 0, regression: 0 };
  results.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });
  const failingSamples = results.filter((r) => !r.is_passing_sample);
  const fixedPct = failingSamples.length > 0
    ? Math.round(((counts.fixed + counts.false_positive) / failingSamples.length) * 100)
    : 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        {counts.fixed > 0 && (
          <Chip label={`${counts.fixed} fixed`} size="small" sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 500,
            color: '#10b981', background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.15)',
          }} />
        )}
        {counts.unfixed > 0 && (
          <Chip label={`${counts.unfixed} unfixed`} size="small" sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 500,
            color: '#ef4444', background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.15)',
          }} />
        )}
        {counts.false_positive > 0 && (
          <Chip label={`${counts.false_positive} false positive`} size="small" sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 500,
            color: '#f59e0b', background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.15)',
          }} />
        )}
        {counts.regression > 0 && (
          <Chip label={`${counts.regression} regression`} size="small" sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 500,
            color: '#f97316', background: 'rgba(249,115,22,0.08)',
            border: '1px solid rgba(249,115,22,0.15)',
          }} />
        )}
      </Box>
      {failingSamples.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={fixedPct}
            sx={{
              flex: 1, height: 6, borderRadius: 3,
              background: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: fixedPct === 100 ? '#10b981' : fixedPct >= 50 ? '#f59e0b' : '#ef4444',
              },
            }}
          />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500, minWidth: 36, textAlign: 'right' }}>
            {fixedPct}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ================================================================== */
/*  Validation checks banner (Coverage / Regression) — prominent,      */
/*  always visible so reviewers can't miss that these checks ran      */
/* ================================================================== */

// Inline "Score: ..." text used in the Regression / Coverage sample section headers.
export function VerdictScore({ verdict, score }) {
  return (
    <span style={{ color: verdictColors(verdict, 0.25).color, fontWeight: 600 }}>
      {verdictScoreText(verdict, score)}
    </span>
  );
}

export function ValidationCheckCard({ label, verdict, score, description }) {
  if (!verdict) return null;
  const pass = verdict === 'PASS';
  const notApplicable = isNotApplicable(verdict);
  const { color, bg, border } = verdictColors(verdict, 0.25);
  const Icon = notApplicable ? InfoOutlinedIcon : pass ? CheckCircleIcon : ReportProblemIcon;
  const pct = Math.round((score || 0) * 100);

  return (
    <Box sx={{
      flex: '1 1 260px', minWidth: 240,
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 2, py: 1.5, borderRadius: 1.5,
      background: bg, border: `1px solid ${border}`,
    }}>
      <Icon sx={{ color, fontSize: 24, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography sx={{ color, fontSize: '0.85rem', fontWeight: 700 }}>
            {notApplicable ? verdict : `${verdict} · ${pct}%`}
          </Typography>
        </Box>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', mt: 0.25, lineHeight: 1.4 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export function ValidationChecksBanner({ ctx }) {
  if (!ctx.coverage_verdict && !ctx.regression_verdict) return null;
  const coverageCount = ctx.coverage_samples?.length || 0;
  const regressionCount = ctx.regression_samples?.length || 0;
  // regression_scored_count / regression_sample_count are absent on older jobs.
  const hasRegressionCounts =
    typeof ctx.regression_scored_count === 'number' &&
    typeof ctx.regression_sample_count === 'number';
  const coverageDescription = ctx.coverage_verdict === 'NOT_APPLICABLE'
    ? 'No unlabeled samples were scored, so this check did not run'
    : `${coverageCount} unlabeled sample${coverageCount !== 1 ? 's' : ''} checked — verifies the config doesn't regress broader coverage`;
  const regressionDescription = ctx.regression_verdict === 'NOT_APPLICABLE'
    ? 'No labeled samples were scored, so this check did not run'
    : hasRegressionCounts
      ? `${ctx.regression_scored_count} of ${ctx.regression_sample_count} labeled sample${ctx.regression_sample_count !== 1 ? 's' : ''} scored — verifies known-good extractions still work`
      : `${regressionCount} labeled sample${regressionCount !== 1 ? 's' : ''} checked — verifies known-good extractions still work`;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, mx: 3, mt: 2, flexWrap: 'wrap' }}>
      <ValidationCheckCard
        label="Coverage Check"
        verdict={ctx.coverage_verdict}
        score={ctx.coverage_score}
        description={coverageDescription}
      />
      <ValidationCheckCard
        label="Regression Check"
        verdict={ctx.regression_verdict}
        score={ctx.regression_score}
        description={regressionDescription}
      />
    </Box>
  );
}

function ParsedConfigCheck({ check }) {
  if (!check) return null;
  const mismatches = check.mismatches || [];
  const parseMisses = Math.max(0, mismatches.length - (check.errored || 0));
  const pass = parseMisses === 0 && (check.errored || 0) === 0;
  const color = pass ? '#10b981' : parseMisses > 0 ? '#ef4444' : '#f59e0b';
  const Icon = pass ? CheckCircleIcon : WarningAmberIcon;

  return (
    <Box sx={{ mx: 3, mt: 1.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25,
        background: pass ? 'rgba(16,185,129,0.06)' : 'rgba(245,158,11,0.06)',
        border: `1px solid ${pass ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.18)'}`,
        borderRadius: 1,
      }}>
        <Icon sx={{ color, fontSize: 18, mt: 0.15, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600 }}>
            Parsed config check · parsed.{check.attribute}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', mt: 0.25 }}>
            {check.matched}/{check.checked} matched
            {parseMisses > 0 ? ` · ${parseMisses} parse mismatch${parseMisses !== 1 ? 'es' : ''}` : ''}
            {check.errored > 0 ? ` · ${check.errored} config error${check.errored !== 1 ? 's' : ''}` : ''}
          </Typography>
        </Box>
      </Box>
      {mismatches.length > 0 && (
        <Box sx={{ mt: 0.75, px: 2 }}>
          {mismatches.map((mismatch, index) => (
            <Box key={index} sx={{
              mb: 0.5, px: 1.5, py: 0.75, borderRadius: 1,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <Typography sx={{
                color: '#60a5fa', fontSize: '0.7rem',
                wordBreak: 'break-all', overflowWrap: 'anywhere',
              }}>
                {String(mismatch.html_url || 'Unknown sample')}
              </Typography>
              <Typography sx={{
                color: mismatch.error ? '#f59e0b' : '#94a3b8',
                fontSize: '0.68rem', mt: 0.2, wordBreak: 'break-all',
              }}>
                {mismatch.error || `Could not parse: ${mismatch.raw_text}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ================================================================== */
/*  ReviewPanel                                                        */
/* ================================================================== */

const NO_SKILL_RESULT_PREFIX = 'AWAITING_REVIEW — no onboarded skill exists';
const NO_SKILL_TOOLTIP = 'Repair was not attempted (no onboarded skill). Onboard a skill, then retry.';
const EMPTY_CONFIG_TOOLTIP = 'The repair config is empty.';

/**
 * Why Approve must be disabled, or '' when it is allowed. Mirrors the healing
 * agent's 409 on approve: repair never attempted (verdict SKILL_NOT_ONBOARDED,
 * or the result text prefix as a fallback) or an empty repair config.
 */
function getApproveBlockReason(job, ctx) {
  const text = [job.result, job.message].find((t) => typeof t === 'string' && t) || '';
  if (ctx.repair_verdict === 'SKILL_NOT_ONBOARDED' || text.startsWith(NO_SKILL_RESULT_PREFIX)) {
    return NO_SKILL_TOOLTIP;
  }
  if (Array.isArray(ctx.repair_config) && ctx.repair_config.length === 0) {
    return EMPTY_CONFIG_TOOLTIP;
  }
  return '';
}

/** Days after a reject that the agent keeps a job's state, so it can still be reopened. */
export const REOPEN_WINDOW_DAYS = 14;
const REOPEN_WINDOW_MS = REOPEN_WINDOW_DAYS * 24 * 60 * 60 * 1000;

export const REOPEN_HINT = `Puts this job back in Awaiting Review so you can edit the config and approve it. Available for ${REOPEN_WINDOW_DAYS} days after the reject.`;

/** True when review_context.rejected_at exists and is within the reopen window. */
export function isWithinReopenWindow(job, now = Date.now()) {
  const at = Date.parse(job?.review_context?.rejected_at);
  return !Number.isNaN(at) && now - at <= REOPEN_WINDOW_MS;
}

/** "Reopened by X on 03 Nov 2026" for a reopened job still awaiting review, else null. */
export function reopenedNote(job) {
  const reopened = job?.review_context?.reopened;
  if (job?.status !== 'awaiting_review' || !reopened || typeof reopened !== 'object') return null;
  const at = reopened.at ? new Date(reopened.at) : null;
  const when = at && !Number.isNaN(at.getTime())
    ? ` on ${at.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
    : '';
  const by = typeof reopened.by === 'string' && reopened.by.trim() ? ` by ${reopened.by.trim()}` : '';
  return `Reopened${by}${when}. Edit the config if needed, then approve to open a PR.`;
}

export default function ReviewPanel({ job, onApprove, onReject, onRevise, onReopen, actionLoading, githubConfigured = true }) {
  const ctx = job.review_context;
  const reopenedText = reopenedNote(job);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState('');

  async function handleReopen() {
    setReopening(true);
    setReopenError('');
    try {
      await onReopen(job.id);
    } catch (err) {
      setReopenError(err?.message || 'Failed to reopen job');
    } finally {
      setReopening(false);
    }
  }
  const [diffOpen, setDiffOpen] = useState(false);
  const [editConfigOpen, setEditConfigOpen] = useState(false);
  const [approvingWithEdits, setApprovingWithEdits] = useState(false);

  async function handleApproveWithEdits(editedConfig, editedParsedConfig) {
    setApprovingWithEdits(true);
    try {
      await onApprove(job.id, editedConfig, editedParsedConfig);
      setEditConfigOpen(false);
    } finally {
      setApprovingWithEdits(false);
    }
  }

  if (!ctx) return null;

  const prUrlMatch = job.message?.match(/https?:\/\/\S+/);
  const prUrl = prUrlMatch ? prUrlMatch[0].replace(/[()]/g, '') : null;
  const hasParsedConfig = Boolean(
    ctx.old_parsed_config ||
    ctx.repair_parsed_config ||
    ctx.parsed_config_file_path,
  );
  const approveBlockReason = getApproveBlockReason(job, ctx);
  const hasAnyConfig = Boolean(ctx.old_config || ctx.repair_config || hasParsedConfig);
  const parsedDiff = alignParsedConfigDiff(
    job.attribute,
    ctx.old_parsed_config,
    ctx.repair_parsed_config || ctx.old_parsed_config,
  );

  return (
    <Box sx={{
      borderTop: '1px solid rgba(255,255,255,0.06)',
      background: 'rgba(15, 23, 42, 0.3)',
    }}>
      {hasAutoApprovalBadge(job) && (
        <Box sx={{ mx: 3, mt: 2 }}>
          <AutoApprovalBadge job={job} />
        </Box>
      )}

      {/* Status banner */}
      {(job.status === 'completed' || job.status === 'approved') && prUrl && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mx: 3, mt: 2, px: 2, py: 1.25,
          background: 'rgba(16,185,129,0.06)', borderRadius: 1, border: '1px solid rgba(16,185,129,0.15)',
        }}>
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', flex: 1 }}>
            Pull request created
          </Typography>
          <Button
            size="small" href={prUrl} target="_blank" rel="noreferrer"
            sx={{ color: '#60a5fa', textTransform: 'none', fontWeight: 500, fontSize: '0.78rem', minWidth: 0, p: 0 }}
          >
            View PR
          </Button>
        </Box>
      )}

      {skippedGateCode(job) && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mx: 3, mt: 2, px: 2, py: 1.25,
          background: 'rgba(148,163,184,0.08)', borderRadius: 1, border: '1px solid rgba(148,163,184,0.25)',
        }}>
          <InfoOutlinedIcon sx={{ color: '#94a3b8', fontSize: 16 }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            Skipped by relevance gate ({skippedGateCode(job)}). No reviewer rejected this job.
          </Typography>
        </Box>
      )}

      {job.status === 'rejected' && job.message && !skippedGateCode(job) && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mx: 3, mt: 2, px: 2, py: 1.25,
          background: 'rgba(239,68,68,0.06)', borderRadius: 1, border: '1px solid rgba(239,68,68,0.15)',
        }}>
          <ErrorIcon sx={{ color: '#ef4444', fontSize: 16 }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            {job.message}
          </Typography>
        </Box>
      )}

      {reopenedText && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mx: 3, mt: 2, px: 2, py: 1.25,
          background: 'rgba(96,165,250,0.06)', borderRadius: 1, border: '1px solid rgba(96,165,250,0.2)',
        }}>
          <InfoOutlinedIcon sx={{ color: '#60a5fa', fontSize: 16 }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            {reopenedText}
          </Typography>
        </Box>
      )}

      {/* Partial repair banner */}
      {ctx.partial_repair && (
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5, mx: 3, mt: 2, px: 2, py: 1.25,
          background: 'rgba(245,158,11,0.06)', borderRadius: 1, border: '1px solid rgba(245,158,11,0.15)',
        }}>
          <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 16, mt: 0.25 }} />
          <Box>
            <Typography sx={{ color: '#f59e0b', fontSize: '0.82rem', fontWeight: 600 }}>
              Partial Fix
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mt: 0.25, lineHeight: 1.5 }}>
              The agent fixed some samples but ran out of budget before completing all of them.
              You can approve this partial fix, revise with feedback to help the agent fix the remaining samples,
              or reject to start over.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Coverage / regression validation checks — prominent, can't-miss banner */}
      <ValidationChecksBanner ctx={ctx} />
      <ParsedConfigCheck check={ctx.parsed_config_check} />
      <HoldoutCard verdict={ctx.holdout_verdict} counts={ctx.holdout_counts} />
      <RelevanceGateCard gate={ctx.relevance_gate} />
      <LinkedFieldNotes ctx={ctx} />

      {/* Summary row */}
      <Box sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          {/* Result text */}
          {job.result && (
            <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.5, flex: 1, minWidth: 200 }}>
              {job.result}
            </Typography>
          )}

          {/* Metadata chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flexShrink: 0 }}>
            {ctx.drift_type && (
              <Chip label={ctx.drift_type} size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: '#60a5fa', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }} />
            )}
            {ctx.repair_verdict && (
              <Chip label={ctx.repair_verdict} size="small"
                sx={{
                  height: 22, fontSize: '0.7rem', fontWeight: 500,
                  color: ctx.repair_verdict === 'VERIFIED' ? '#34d399'
                    : ctx.repair_verdict === 'PARTIALLY_VERIFIED' ? '#f59e0b'
                    : '#ef4444',
                  background: ctx.repair_verdict === 'VERIFIED' ? 'rgba(16,185,129,0.08)'
                    : ctx.repair_verdict === 'PARTIALLY_VERIFIED' ? 'rgba(245,158,11,0.08)'
                    : 'rgba(239,68,68,0.08)',
                  border: ctx.repair_verdict === 'VERIFIED' ? '1px solid rgba(16,185,129,0.15)'
                    : ctx.repair_verdict === 'PARTIALLY_VERIFIED' ? '1px solid rgba(245,158,11,0.15)'
                    : '1px solid rgba(239,68,68,0.15)',
                }} />
            )}
            {ctx.repair_attempts != null && (
              <Chip label={`${ctx.repair_attempts} attempt${ctx.repair_attempts !== 1 ? 's' : ''}`} size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
            )}
            {ctx.repair_tool_calls != null && (
              <Chip label={`${ctx.repair_tool_calls} tool calls`} size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
            )}
            {ctx.revision_count > 0 && (
              <Chip label={`${ctx.revision_count} revision${ctx.revision_count !== 1 ? 's' : ''}`} size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
            )}
            {(ctx.token_usage?.estimated_cost_usd != null) && (
              <Chip label={`$${ctx.token_usage.estimated_cost_usd.toFixed(4)}`} size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
            )}
          </Box>
        </Box>

        {/* Config diff / edit buttons */}
        {hasAnyConfig && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setDiffOpen(true)}
              sx={{
                color: '#94a3b8', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                px: 1.5, py: 0.5, borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.08)',
                '&:hover': { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.15)' },
              }}
            >
              View Config Diff
            </Button>
            {job.status === 'awaiting_review' && (
              <Button
                size="small"
                onClick={() => setEditConfigOpen(true)}
                sx={{
                  color: '#60a5fa', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                  px: 1.5, py: 0.5, borderRadius: 1,
                  border: '1px solid rgba(59,130,246,0.25)',
                  '&:hover': { background: 'rgba(59,130,246,0.06)', borderColor: 'rgba(59,130,246,0.4)' },
                }}
              >
                Edit Config
              </Button>
            )}

            {/* Full-screen diff overlay */}
            <Dialog
              open={diffOpen}
              onClose={() => setDiffOpen(false)}
              maxWidth={false}
              fullWidth
              PaperProps={{
                sx: {
                  background: '#0f172a',
                  color: '#e2e8f0',
                  border: '1px solid rgba(148,163,184,0.12)',
                  borderRadius: 2,
                  width: '95vw',
                  maxWidth: '95vw',
                  height: '85vh',
                  maxHeight: '85vh',
                  m: 2,
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >
              {/* Header */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 3, py: 1.5, borderBottom: '1px solid rgba(148,163,184,0.08)',
                flexShrink: 0,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#e2e8f0' }}>
                    Config Diff
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: '0.8rem' }}>
                    {job.retailer} / {job.attribute}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDiffOpen(false)} size="small" sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Diff content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {hasParsedConfig ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <JsonDiffViewer
                      oldConfig={ctx.old_config || []}
                      newConfig={ctx.repair_config || []}
                      title="Extraction config"
                    />
                    <Box>
                      <JsonDiffViewer
                        oldConfig={parsedDiff.oldConfig}
                        newConfig={parsedDiff.newConfig}
                        title="Parsed config"
                      />
                      {ctx.parsed_config_file_path && (
                        <Typography sx={{
                          color: '#64748b', fontSize: '0.7rem', fontFamily: 'monospace',
                          mt: 0.75, wordBreak: 'break-all',
                        }}>
                          {ctx.parsed_config_file_path}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <JsonDiffViewer oldConfig={ctx.old_config || []} newConfig={ctx.repair_config || []} />
                )}
              </Box>
            </Dialog>

            {/* Edit config modal */}
            <EditConfigModal
              open={editConfigOpen}
              onClose={() => setEditConfigOpen(false)}
              jobId={job.id}
              job={job}
              initialConfig={ctx.repair_config}
              initialParsedConfig={ctx.repair_parsed_config || ctx.old_parsed_config}
              parsedConfigPath={ctx.parsed_config_file_path}
              showParsedConfig={hasParsedConfig}
              onApproveWithEdits={handleApproveWithEdits}
              approving={approvingWithEdits}
              // Gate only the no-skill case here: the agent allows a non-empty
              // editedConfig on an empty-config job, so that reason stays on plain Approve.
              approveBlockReason={approveBlockReason === NO_SKILL_TOOLTIP ? NO_SKILL_TOOLTIP : ''}
            />
          </Box>
        )}
      </Box>

      {/* ── Per-sample results (primary, always open) ──────────── */}
      {ctx.per_sample_results && ctx.per_sample_results.length > 0 && (
        <Section title="Sample Results" defaultOpen count={`${ctx.per_sample_results.length} samples`}>
          <SampleResultsSummary results={ctx.per_sample_results} />

          {/* Failing / fixed / unfixed / false-positive samples */}
          {ctx.per_sample_results.filter((r) => !r.is_passing_sample).length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', fontWeight: 600, mb: 0.75 }}>
                Target Samples
              </Typography>
              {ctx.per_sample_results
                .filter((r) => !r.is_passing_sample)
                .map((r, i) => (
                  <SampleResultRow
                    key={`fail-${i}`}
                    result={r}
                    parsed={parsedForSample(ctx.parsed_config_check, r)}
                  />
                ))}
            </Box>
          )}

          {/* Regression test samples */}
          {ctx.per_sample_results.filter((r) => r.is_passing_sample).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', fontWeight: 600, mb: 0.75 }}>
                Regression Tests ({ctx.per_sample_results.filter((r) => r.is_passing_sample).length})
              </Typography>
              {ctx.per_sample_results
                .filter((r) => r.is_passing_sample)
                .map((r, i) => (
                  <SampleResultRow
                    key={`pass-${i}`}
                    result={r}
                    parsed={parsedForSample(ctx.parsed_config_check, r)}
                  />
                ))}
            </Box>
          )}
        </Section>
      )}

      {/* ── Fallback: show failing + passing when no per-sample results ─ */}
      {(!ctx.per_sample_results || ctx.per_sample_results.length === 0) && (
        <>
          {ctx.failing_samples && ctx.failing_samples.length > 0 && (
            <Section title="Failing Samples" defaultOpen count={`${ctx.failing_samples.length}`}>
              {ctx.failing_samples.map((s, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.75, py: 1, px: 1.5,
                  background: s.is_false_positive ? 'rgba(245,158,11,0.06)' : 'rgba(239,68,68,0.06)',
                  border: `1px solid ${s.is_false_positive ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)'}`,
                  borderRadius: 1,
                }}>
                  {s.is_false_positive
                    ? <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 15, mt: 0.2 }} />
                    : <ErrorIcon sx={{ color: '#ef4444', fontSize: 15, mt: 0.2 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                        #{i + 1} {s.is_false_positive ? '(false positive)' : ''}
                      </Typography>
                    </Box>
                    <a href={s.html_url || s.url} target="_blank" rel="noreferrer"
                      style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '0.78rem', wordBreak: 'break-all', display: 'block', marginTop: 2 }}>
                      {s.html_url || s.url}
                    </a>
                    {s.expected_value && (
                      <Typography sx={{
                        color: '#64748b', fontSize: '0.72rem', mt: 0.25,
                        wordBreak: 'break-all', overflowWrap: 'anywhere',
                      }}>
                        <span style={{ color: '#475569' }}>expected:</span> {typeof s.expected_value === 'string' ? s.expected_value : JSON.stringify(s.expected_value)}
                      </Typography>
                    )}
                    {s.extracted_value && (
                      <Typography sx={{
                        color: '#64748b', fontSize: '0.72rem',
                        wordBreak: 'break-all', overflowWrap: 'anywhere',
                      }}>
                        <span style={{ color: '#475569' }}>got:</span> {typeof s.extracted_value === 'string' ? s.extracted_value : JSON.stringify(s.extracted_value)}
                      </Typography>
                    )}
                    {s.diagnostic_hint && (
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontStyle: 'italic', mt: 0.25 }}>
                        {s.diagnostic_hint}
                      </Typography>
                    )}
                    {s.false_positive_reason && (
                      <Typography sx={{ color: '#f59e0b', fontSize: '0.72rem', fontStyle: 'italic', mt: 0.25 }}>
                        {s.false_positive_reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Section>
          )}
          {ctx.passing_samples && ctx.passing_samples.length > 0 && (
            <Section title="Passing Samples (Regression Tests)" count={`${ctx.passing_samples.length}`}>
              {ctx.passing_samples.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, fontSize: '0.8rem' }}>
                  <CheckCircleIcon sx={{ color: '#64748b', fontSize: 14 }} />
                  <Typography sx={{ color: '#475569', fontSize: '0.72rem', minWidth: 18, textAlign: 'right' }}>{i + 1}.</Typography>
                  <a href={s.html_url || s.url} target="_blank" rel="noreferrer"
                    style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                    {s.html_url || s.url}
                  </a>
                  {s.expected_value && (
                    <Typography sx={{ color: '#475569', fontSize: '0.72rem', ml: 0.5 }}>
                      expected: {s.expected_value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Section>
          )}
        </>
      )}

      {/* ── Coverage samples (Tier B) ─────────────────────────── */}
      {ctx.coverage_samples && ctx.coverage_samples.length > 0 && (
        <Section
          title="Coverage Samples (Tier B)"
          count={`${ctx.coverage_samples.length} unlabeled`}
        >
          <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mb: 1.25, lineHeight: 1.5 }}>
            Products that recently had a non-null value but are now null — used to verify the config doesn't regress broader coverage.
            {ctx.coverage_verdict && (
              <> Score: <VerdictScore verdict={ctx.coverage_verdict} score={ctx.coverage_score} /></>
            )}
          </Typography>
          {ctx.coverage_samples.map((s, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.75, py: 1, px: 1.5,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 1,
              overflow: 'hidden',
            }}>
              <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontFamily: 'monospace', minWidth: 18, mt: 0.1 }}>
                {i + 1}.
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <a href={s.html_url || s.url} target="_blank" rel="noreferrer"
                  style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '0.78rem', wordBreak: 'break-all', overflowWrap: 'anywhere', display: 'block' }}>
                  {s.html_url || s.url}
                </a>
                {s.extracted_value != null && (
                  <Typography sx={{
                    color: '#64748b', fontSize: '0.72rem', mt: 0.25,
                    wordBreak: 'break-all', overflowWrap: 'anywhere',
                  }}>
                    <span style={{ color: '#475569' }}>extracted:</span>{' '}
                    {typeof s.extracted_value === 'string' ? s.extracted_value : JSON.stringify(s.extracted_value)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Section>
      )}

      {/* ── Regression samples (Tier A) ────────────────────────── */}
      {ctx.regression_samples && ctx.regression_samples.length > 0 && (
        <Section
          title="Regression Samples (Tier A)"
          count={`${ctx.regression_samples.length} labeled`}
        >
          <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mb: 1.25, lineHeight: 1.5 }}>
            Products with existing non-null values — used to verify the config maintains correctness on known-good extractions.
            {ctx.regression_verdict && (
              <> Score: <VerdictScore verdict={ctx.regression_verdict} score={ctx.regression_score} /></>
            )}
          </Typography>
          {ctx.regression_samples.map((s, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.75, py: 1, px: 1.5,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 1,
              overflow: 'hidden',
            }}>
              <Typography sx={{ color: '#475569', fontSize: '0.7rem', fontFamily: 'monospace', minWidth: 18, mt: 0.1 }}>
                {i + 1}.
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <a href={s.html_url || s.url} target="_blank" rel="noreferrer"
                  style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '0.78rem', wordBreak: 'break-all', overflowWrap: 'anywhere', display: 'block' }}>
                  {s.html_url || s.url}
                </a>
                {s.expected_value != null && (
                  <Typography sx={{
                    color: '#64748b', fontSize: '0.72rem', mt: 0.25,
                    wordBreak: 'break-all', overflowWrap: 'anywhere',
                  }}>
                    <span style={{ color: '#475569' }}>db value:</span>{' '}
                    {typeof s.expected_value === 'string' ? s.expected_value : JSON.stringify(s.expected_value)}
                  </Typography>
                )}
                {s.extracted_value != null && (
                  <Typography sx={{
                    color: '#64748b', fontSize: '0.72rem', mt: 0.25,
                    wordBreak: 'break-all', overflowWrap: 'anywhere',
                  }}>
                    <span style={{ color: '#475569' }}>extracted:</span>{' '}
                    {typeof s.extracted_value === 'string' ? s.extracted_value : JSON.stringify(s.extracted_value)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Section>
      )}

      {/* ── Collapsible sections ───────────────────────────────── */}
      {ctx.agent_summary && (
        <Section title="Agent Summary" defaultOpen>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {ctx.agent_summary}
          </Typography>
        </Section>
      )}
      {ctx.repair_reasoning && (
        <Section title="Verification Result" defaultOpen={!ctx.agent_summary}>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {ctx.repair_reasoning}
          </Typography>
        </Section>
      )}

      {ctx.revision_history && ctx.revision_history.length > 0 && (
        <Section title="Revision History" count={`${ctx.revision_history.length}`}>
          {ctx.revision_history.map((rev, i) => (
            <Box key={i} sx={{
              mb: 0.75, px: 2, py: 1,
              background: 'rgba(255,255,255,0.02)',
              borderRadius: 1,
              borderLeft: '2px solid rgba(255,255,255,0.06)',
              fontSize: '0.8rem',
              color: '#94a3b8',
            }}>
              {typeof rev === 'string' ? rev : JSON.stringify(rev, null, 2)}
            </Box>
          ))}
        </Section>
      )}

      {/* Reopen a human-rejected job (not a gate skip) inside the window; older rejects can't be reopened (the agent deletes their state) */}
      {job.status === 'rejected' && !skippedGateCode(job) && onReopen && isWithinReopenWindow(job) && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', gap: 0.75, px: 3, py: 2,
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined" size="small"
              disabled={actionLoading || reopening} onClick={handleReopen}
              sx={{
                color: '#60a5fa', borderColor: 'rgba(96,165,250,0.6)', fontWeight: 500,
                textTransform: 'none', px: 2.5, fontSize: '0.82rem',
                '&:hover': { borderColor: '#60a5fa', background: 'rgba(96,165,250,0.12)' },
                '&.Mui-disabled': { color: 'rgba(96,165,250,0.4)', borderColor: 'rgba(96,165,250,0.25)' },
              }}
            >
              Reopen for review
            </Button>
            <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              {REOPEN_HINT}
            </Typography>
          </Box>
          {reopenError && (
            <Typography role="alert" sx={{ color: '#ef4444', fontSize: '0.78rem' }}>
              {reopenError}
            </Typography>
          )}
        </Box>
      )}

      {/* Action buttons */}
      {job.status === 'awaiting_review' && (
        <Box sx={{
          display: 'flex', gap: 1.5, px: 3, py: 2,
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}>
          <Tooltip
            title={approveBlockReason
              || (githubConfigured ? '' : 'Configure your GitHub PAT in Settings to approve jobs')}
            arrow
          >
            <span>
              <Button
                variant="contained" size="small"
                disabled={actionLoading || !githubConfigured || Boolean(approveBlockReason)}
                onClick={() => onApprove(job.id)}
                sx={{
                  background: '#10b981', fontWeight: 500, textTransform: 'none', px: 2.5,
                  fontSize: '0.82rem', boxShadow: 'none',
                  '&:hover': { background: '#059669', boxShadow: 'none' },
                  '&.Mui-disabled': { background: 'rgba(16, 185, 129, 0.3)', color: 'rgba(255,255,255,0.4)' },
                }}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained" size="small"
            disabled={actionLoading} onClick={() => onReject(job.id)}
            sx={{
              background: '#ef4444', fontWeight: 500, textTransform: 'none', px: 2.5,
              fontSize: '0.82rem', boxShadow: 'none',
              '&:hover': { background: '#dc2626', boxShadow: 'none' },
            }}
          >
            Reject
          </Button>
          <Button
            variant="outlined" size="small"
            disabled={actionLoading} onClick={() => onRevise(job.id)}
            sx={{
              color: '#60a5fa', borderColor: 'rgba(96,165,250,0.6)', fontWeight: 500,
              textTransform: 'none', px: 2.5, fontSize: '0.82rem',
              '&:hover': { borderColor: '#60a5fa', background: 'rgba(96,165,250,0.12)' },
              '&.Mui-disabled': { color: 'rgba(96,165,250,0.4)', borderColor: 'rgba(96,165,250,0.25)' },
            }}
          >
            Revise
          </Button>
        </Box>
      )}
    </Box>
  );
}
